import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import { colors, borderRadius, sxPresets } from '@/theme'
import type { PatientDetails } from '@/services/patients'

interface SafeguardingQuestionsProps {
  patient: PatientDetails
}

export default function SafeguardingQuestions({ patient }: SafeguardingQuestionsProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const safeguardingData = useMemo(() => {
    // Pick the most recent support entry with safeguarding questions
    const mostRecentSupportEntry = patient.support
      .filter(s => !!s.safeguardingQuestions) // Filter out support items with no safeguarding questions
      .sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime())[0] // Sort by createdAt in descending order
    if (!mostRecentSupportEntry?.safeguardingQuestions) return null

    try {
      const parsed = JSON.parse(mostRecentSupportEntry.safeguardingQuestions) as Record<string, string>
      const entries = Object.entries(parsed)
      return entries.length > 0 ? entries : null
    } catch {
      return [['Invalid Data Detected', mostRecentSupportEntry.safeguardingQuestions]]
    }
  }, [patient.support])

  return (
    <Paper
      id='safeguarding-questions'
      elevation={1}
      sx={{ border: `1px solid ${colors.border.default}`, borderRadius: borderRadius.lg, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={sxPresets().section.header}>
        <Box sx={{ cursor: 'pointer', flex: 1 }} onClick={() => setIsExpanded(!isExpanded)}>
          <Typography variant='sectionTitle'>Safeguarding questions</Typography>
        </Box>
        <IconButton
          size='small'
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            ...sxPresets().expandButton,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        >
          <ExpandMore />
        </IconButton>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ ...sxPresets().section.content, px: 4, pb: 4 }}>
          {!safeguardingData ? (
            <Typography sx={{ color: colors.text.secondary, fontSize: '14px', py: 2 }}>
              No safeguarding questions available
            </Typography>
          ) : (
            <>
              {/* Table Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr',
                  gap: 2,
                  pb: 2,
                  borderBottom: `1px solid ${colors.border.default}`
                }}
              >
                <Typography sx={{ fontWeight: 700, color: colors.text.primary, fontSize: '14px' }}>Question</Typography>
                <Typography sx={{ fontWeight: 700, color: colors.text.primary, fontSize: '14px' }}>Answer</Typography>
              </Box>

              {/* Question Rows */}
              {safeguardingData.map(([question, answer], index) => (
                <Box
                  key={question}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '3fr 1fr',
                    gap: 2,
                    py: 2.5,
                    borderBottom: index === safeguardingData.length - 1 ? 'none' : `1px solid ${colors.border.default}`
                  }}
                >
                  <Typography sx={{ color: colors.text.primary, fontSize: '14px', lineHeight: 1.7 }}>
                    {question}
                  </Typography>
                  <Typography sx={{ color: colors.text.secondary, fontSize: '14px', lineHeight: 1.7 }}>
                    {answer || '-'}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}
